import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
} from '@mui/material';
import api, { formatApiErrorMessage, skipAuthRedirectConfig } from '../../services/api';

export type DocumentShareMode = 'copy' | 'readOnly';

interface ShareDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onShared: () => void;
  documentName: string;
}

const ShareDocumentDialog: React.FC<ShareDocumentDialogProps> = ({
  open,
  onClose,
  onShared,
  documentName,
}) => {
  const [shareMode, setShareMode] = useState<DocumentShareMode>('copy');
  const [targetUsername, setTargetUsername] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setShareMode('copy');
      setTargetUsername('');
      setMessage('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleShare = async () => {
    const username = targetUsername.trim();
    if (!username) {
      setError('Username is required');
      return;
    }
    if (!documentName.trim()) {
      setError('Document name is missing');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const requestConfig = skipAuthRedirectConfig();
      const payload = {
        documentId: documentName,
        message: message.trim() || undefined,
      };
      if (shareMode === 'copy') {
        await api.post(
          '/documents/shares',
          { ...payload, targetUsername: username },
          requestConfig,
        );
      } else {
        await api.post(
          '/documents/read-access',
          { ...payload, viewerUsername: username },
          requestConfig,
        );
      }
      onClose();
      onShared();
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: unknown } })?.response;
      const msg = formatApiErrorMessage(response?.data, 'Failed to share document');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUsernameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleShare();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Share document</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Document"
          value={documentName}
          fullWidth
          disabled
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sharing type
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={shareMode}
            onChange={(_, value: DocumentShareMode | null) => {
              if (value) setShareMode(value);
            }}
            disabled={submitting}
          >
            <ToggleButton value="copy">Copy to library</ToggleButton>
            <ToggleButton value="readOnly">Live view only</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField
          margin="dense"
          label={shareMode === 'copy' ? 'Share with (username or email)' : 'Grant view to (username or email)'}
          value={targetUsername}
          onChange={(e) => setTargetUsername(e.target.value)}
          onKeyDown={handleUsernameKeyDown}
          fullWidth
          disabled={submitting}
          helperText={
            shareMode === 'copy'
              ? 'Username or email; not case-sensitive. The user receives their own copy they can edit.'
              : 'Username or email; not case-sensitive. The user can open your live document read-only until you revoke access.'
          }
        />
        <TextField
          margin="dense"
          label="Message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          disabled={submitting}
          sx={{ mt: 1 }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" onClick={() => void handleShare()} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={22} /> : shareMode === 'copy' ? 'Share copy' : 'Grant view'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDocumentDialog;
